// AcessoRastreioMeusPedidosHavox.tsx
import React, { useMemo, useState } from "react";
import Table from "./TableRastreioCliente";
import TitleButtons from "../../../components/TitleButtons";
import { useNavigate } from "react-router-dom";
import { useListaCasesLaboratorioSolicitante } from "./solicitacoesData";

import JSZip from "jszip";
import { saveAs } from "file-saver";
import TableActions from "../../../components/TableActions";

function safe(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s === "" ? "N/A" : s;
}

function formatCaseRef(v: any) {
  const s = safe(v);
  if (s === "N/A") return s;

  const parts = s.split("-").filter((p) => p !== "");
  if (parts.length >= 3) return parts.slice(0, 3).join("-");
  return s;
}

function getInitials(name: string) {
  const clean = safe(name);
  if (clean === "N/A") return "?";

  const parts = clean
    .split(/\s+/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function avatarBgByIndex(index: number) {
  const colors = [
    "#033B05",
    "#059669",
    "#B45309",
    "#7C3AED",
    "#BE123C",
    "#0F766E",
    "#033E2A",
    "#475569",
  ];
  return colors[index % colors.length];
}

function CopyBadges({ names }: { names: string[] }) {
  const validNames = (names ?? []).filter((n) => safe(n) !== "N/A");
  if (!validNames.length) return <span style={{ color: "#9CA3AF" }}>—</span>;

  const tooltip = validNames.join(", ");

  return (
    <div
      title={tooltip}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 0,
        minHeight: 28,
      }}
    >
      {validNames.map((name, idx) => (
        <div
          key={`${name}-${idx}`}
          title={name}
          style={{
            width: 26,
            height: 26,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 600,
            color: "#fff",
            background: avatarBgByIndex(idx),
            border: "2px solid #fff",
            marginLeft: idx === 0 ? 0 : -6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            userSelect: "none",
          }}
          aria-label={name}
        >
          {getInitials(name)}
        </div>
      ))}
    </div>
  );
}

async function saveBlobAs(blob: Blob, fileName: string) {
  const anyWin = window as any;

  if (anyWin.showSaveFilePicker) {
    const handle = await anyWin.showSaveFilePicker({
      suggestedName: fileName,
      types: [
        {
          description: "Planilha Excel (.xlsx)",
          accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [".xlsx"],
          },
        },
      ],
    });

    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  saveAs(blob, fileName);
}

async function gerarXlsxModel2(params: {
  rows: Array<{
    caseRef: string;
    embarcacao: string;
    equipamento: string;
    solicitante: string;
    emCopia: string;
    status: string;
  }>;
  fileName: string;
}) {
  const res = await fetch("/model2.xlsx", { cache: "no-store" });
  if (!res.ok)
    throw new Error(`Falha ao baixar model2.xlsx (HTTP ${res.status})`);

  const buf = await res.arrayBuffer();

  const sig = new Uint8Array(buf.slice(0, 2));
  const isZip = sig[0] === 0x50 && sig[1] === 0x4b;
  if (!isZip) {
    const text = new TextDecoder().decode(buf.slice(0, 200));
    throw new Error(
      `model2.xlsx não foi servido como arquivo.\nInício: ${text}`
    );
  }

  const zip = await JSZip.loadAsync(buf);
  const MAIN_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";

  const sharedStringsPath = "xl/sharedStrings.xml";
  if (!zip.file(sharedStringsPath)) {
    throw new Error(
      "Seu model2.xlsx não possui xl/sharedStrings.xml. Este gerador espera sharedStrings."
    );
  }

  const sharedXml = await zip.file(sharedStringsPath)!.async("string");
  const sharedDoc = new DOMParser().parseFromString(
    sharedXml,
    "application/xml"
  );

  const sst = sharedDoc.getElementsByTagNameNS(MAIN_NS, "sst")[0];
  if (!sst) throw new Error("sharedStrings.xml inválido (sem <sst>).");

  const siList = Array.from(sharedDoc.getElementsByTagNameNS(MAIN_NS, "si"));

  const getSiText = (si: Element) => {
    const tNodes = Array.from(si.getElementsByTagNameNS(MAIN_NS, "t"));
    return tNodes.map((t) => t.textContent ?? "").join("");
  };

  const strings: string[] = siList.map(getSiText);
  const map = new Map<string, number>();
  strings.forEach((s, i) => map.set(s, i));

  const ensureSharedIndex = (value: string) => {
    const v = value ?? "";
    if (map.has(v)) return map.get(v)!;

    const idx = strings.length;
    strings.push(v);
    map.set(v, idx);

    const si = sharedDoc.createElementNS(MAIN_NS, "si");
    const t = sharedDoc.createElementNS(MAIN_NS, "t");
    t.setAttribute("xml:space", "preserve");
    t.textContent = v;
    si.appendChild(t);
    sst.appendChild(si);

    return idx;
  };

  const updateSstCounts = () => {
    sst.setAttribute("count", String(strings.length));
    sst.setAttribute("uniqueCount", String(strings.length));
  };

  const sheetPath = "xl/worksheets/sheet1.xml";
  if (!zip.file(sheetPath))
    throw new Error("Template sem xl/worksheets/sheet1.xml");

  const sheetXml = await zip.file(sheetPath)!.async("string");
  const sheetDoc = new DOMParser().parseFromString(sheetXml, "application/xml");

  const sheetData = sheetDoc.getElementsByTagNameNS(MAIN_NS, "sheetData")[0];
  if (!sheetData) throw new Error("sheet1.xml inválido (sem <sheetData>).");

  const getRowByNumber = (rnum: number) => {
    const rows = Array.from(sheetDoc.getElementsByTagNameNS(MAIN_NS, "row"));
    return rows.find((r) => Number(r.getAttribute("r")) === rnum) || null;
  };

  const cloneRowWithNewNumber = (templateRow: Element, newR: number) => {
    const cloned = templateRow.cloneNode(true) as Element;
    cloned.setAttribute("r", String(newR));

    const cells = Array.from(cloned.getElementsByTagNameNS(MAIN_NS, "c"));
    for (const c of cells) {
      const ref = c.getAttribute("r") || "";
      const col = ref.replace(/[0-9]/g, "");
      c.setAttribute("r", `${col}${newR}`);

      while (c.firstChild) c.removeChild(c.firstChild);
      c.removeAttribute("t");
    }

    return cloned;
  };

  const ensureRowExists = (rnum: number) => {
    let row = getRowByNumber(rnum);
    if (row) return row;

    const allRows = Array.from(sheetDoc.getElementsByTagNameNS(MAIN_NS, "row"));
    const dataRows = allRows
      .map((r) => Number(r.getAttribute("r") || "0"))
      .filter((n) => n >= 8)
      .sort((a, b) => a - b);

    const templateR = dataRows.length ? dataRows[dataRows.length - 1] : 8;
    const templateRow = getRowByNumber(templateR);
    if (!templateRow)
      throw new Error("Não encontrei linha modelo (>=8) no template.");

    const newRow = cloneRowWithNewNumber(templateRow, rnum);
    sheetData.appendChild(newRow);
    return newRow;
  };

  const ensureCell = (row: Element, col: string, rnum: number) => {
    const ref = `${col}${rnum}`;
    const cells = Array.from(row.getElementsByTagNameNS(MAIN_NS, "c"));
    let cell = cells.find((c) => c.getAttribute("r") === ref) || null;

    if (!cell) {
      cell = sheetDoc.createElementNS(MAIN_NS, "c");
      cell.setAttribute("r", ref);

      const first = cells[0];
      const s = first?.getAttribute("s");
      if (s) cell.setAttribute("s", s);

      row.appendChild(cell);
    }
    return cell;
  };

  const setCellSharedString = (cell: Element, sharedIndex: number) => {
    cell.setAttribute("t", "s");
    while (cell.firstChild) cell.removeChild(cell.firstChild);

    const v = sheetDoc.createElementNS(MAIN_NS, "v");
    v.textContent = String(sharedIndex);
    cell.appendChild(v);
  };

  const startRow = 8;

  for (let i = 0; i < params.rows.length; i++) {
    const rnum = startRow + i;
    const row = ensureRowExists(rnum);

    const item = params.rows[i];

    const a = ensureSharedIndex(item.caseRef);
    const b = ensureSharedIndex(item.embarcacao);
    const c = ensureSharedIndex(item.equipamento);
    const d = ensureSharedIndex(item.solicitante);
    const e = ensureSharedIndex(item.emCopia);
    const g = ensureSharedIndex(item.status);

    setCellSharedString(ensureCell(row, "A", rnum), a);
    setCellSharedString(ensureCell(row, "B", rnum), b);
    setCellSharedString(ensureCell(row, "C", rnum), c);
    setCellSharedString(ensureCell(row, "D", rnum), d);
    setCellSharedString(ensureCell(row, "E", rnum), e);
    setCellSharedString(ensureCell(row, "G", rnum), g);
  }

  const dim = sheetDoc.getElementsByTagNameNS(MAIN_NS, "dimension")[0];
  if (dim) {
    const lastRow = Math.max(startRow, startRow + params.rows.length - 1);
    dim.setAttribute("ref", `A1:G${lastRow}`);
  }

  updateSstCounts();

  const sharedOut = new XMLSerializer().serializeToString(sharedDoc);
  const sheetOut = new XMLSerializer().serializeToString(sheetDoc);

  zip.file(sharedStringsPath, sharedOut);
  zip.file(sheetPath, sheetOut);

  const outBuf = await zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
  });

  const blob = new Blob([outBuf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  await saveBlobAs(blob, params.fileName);
}

const AcessoRastreioMeusPedidosHavox: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useListaCasesLaboratorioSolicitante();

  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return itens ?? [];

    return (itens ?? []).filter((x: any) => {
      const caseRef = String(x.caseRef ?? "").toLowerCase();
      const emb = String(x.embarcacaoNome ?? "").toLowerCase();
      const equip = String(x.equipamentoNome ?? "").toLowerCase();
      const token = String(x.publicToken ?? "").toLowerCase();
      const desc = String(x.descricaoProblema ?? "").toLowerCase();
      const status = String(x.statusAtualNome ?? "").toLowerCase();
      const solicitante = String(
        x.solicitantePrincipalNome ?? ""
      ).toLowerCase();
      const emCopia = Array.isArray(x.copiadosNomes)
        ? x.copiadosNomes.join(", ").toLowerCase()
        : "";

      return (
        caseRef.includes(query) ||
        emb.includes(query) ||
        equip.includes(query) ||
        token.includes(query) ||
        desc.includes(query) ||
        status.includes(query) ||
        solicitante.includes(query) ||
        emCopia.includes(query)
      );
    });
  }, [itens, q]);

  const tableItems = useMemo(() => {
    return (filtered ?? []).map((p: any) => {
      const copiadosNomes = Array.isArray(p?.copiadosNomes)
        ? p.copiadosNomes
        : [];

      return {
        publicToken: safe(p.publicToken),
        caseRef: formatCaseRef(p.caseRef),
        embarcacaoNome: safe(p.embarcacaoNome),
        equipamentoNome: safe(p.equipamentoNome),
        solicitante: safe(p.solicitantePrincipalNome),
        emCopia: <CopyBadges names={copiadosNomes} />,
        emCopiaTexto: copiadosNomes.join(", ").trim() || "N/A",
        statusAtualNome: safe(p.statusAtualNome),
      };
    });
  }, [filtered]);

  const tokensByOriginalIndex = useMemo(
    () => tableItems.map((x) => x.publicToken),
    [tableItems]
  );

  const headers = [
    "Case",
    "Embarcação",
    "Equipamento",
    "Solicitante",
    "Em cópia",
    "Status atual",
  ];

  const columnWidths = ["220px", "320px", "420px", "240px", "150px", "240px"];

  const rows = useMemo(() => {
    return tableItems.map((x) => [
      x.caseRef,
      x.embarcacaoNome,
      x.equipamentoNome,
      x.solicitante,
      x.emCopia,
      x.statusAtualNome,
    ]);
  }, [tableItems]);

  const handleGerarExcel = async () => {
    try {
      const dataRows = tableItems.map((x) => ({
        caseRef: x.caseRef,
        embarcacao: x.embarcacaoNome,
        equipamento: x.equipamentoNome,
        solicitante: x.solicitante,
        emCopia: x.emCopiaTexto,
        status: x.statusAtualNome,
      }));

      if (!dataRows.length) {
        alert("Não há cases para exportar.");
        return;
      }

      const fileName = `LOGVI-EQUIPAMENTOS-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      await gerarXlsxModel2({ rows: dataRows, fileName });
    } catch (e: any) {
      alert(e?.message || "Falha ao gerar Excel.");
    }
  };

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title={"Acompanhamento do Status dos Serviços - Laboratório"}
      />

      <TableActions
        buttons={[
          {
            label: "Exportar p/ Excel",
            onClick: handleGerarExcel,
            variant: "primary",
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando…</div>
      ) : (
        <Table
          showFinanceFilter={false}
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          companyColumnIndex={-1}
          onDoubleClick={(_viewIndex, _rowData, originalIndex) => {
            const idx =
              typeof originalIndex === "number" ? originalIndex : null;
            if (idx == null || idx < 0) return;

            const token = String(tokensByOriginalIndex[idx] ?? "").trim();
            if (!token || token === "N/A") return;

            navigate(
              `/minhas-solicitacoes/laboratorio-cases/timeline/${encodeURIComponent(
                token
              )}`
            );
          }}
          responsiveWidths={{
            1500: {
              1: "180px",
              2: "280px",
              3: "340px",
              4: "210px",
              5: "130px",
              6: "150px",
              7: "210px",
            },
            1300: {
              1: "160px",
              2: "240px",
              3: "280px",
              4: "180px",
              5: "120px",
              6: "140px",
              7: "190px",
            },
          }}
        />
      )}
    </div>
  );
};

export default AcessoRastreioMeusPedidosHavox;
